import React from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Table from '@amzn/awsui-components-console/table';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import styles from '../StringGroupDetail.module.scss';

interface CxIdDiffTableProps {
  previousCxIds: string[];
  currentCxIds: string[];
  /** Already-localized header for the left ("before") column. */
  previousHeader: string;
  /** Already-localized header for the right ("after") column. */
  currentHeader: string;
}

/**
 * The CX IDs section is a single-row, two-column table, so it needs exactly one
 * table item. Defined once at module scope so the array identity is stable
 * across renders and never triggers a needless table redraw.
 */
const CX_ID_TABLE_ITEMS = [{ id: 'cx-ids' }];

/**
 * Renders a version's CX ID membership as neutral badges, or an em-dash when
 * the version targeted no CX IDs. Neutral (not add/remove colored) because each
 * column shows a full membership snapshot, not a delta — the change is conveyed
 * by comparing the two columns, mirroring the product mock.
 *
 * CX IDs are TRANSCOM identifiers and the em-dash is a symbol, so neither is
 * localized.
 */
const CxIdBadgeList: React.FC<{ cxIds: string[] }> = ({ cxIds }) => {
  // Deduplicated here rather than by each caller, so a backend that repeats a
  // CX ID cannot produce duplicate React keys in one caller and not the other.
  const unique = [...new Set(cxIds)];
  return unique.length > 0 ? (
    <div className={styles.cxIdBadgeList}>
      {unique.map((cxId) => (
        <Badge key={cxId} color="grey">
          {cxId}
        </Badge>
      ))}
    </div>
  ) : (
    <Box color="text-body-secondary">—</Box>
  );
};

/**
 * Side-by-side CX ID membership for two versions. Shared by DiffModal
 * (version-to-version comparison) and RollbackModal (rollback impact preview)
 * so the two never drift in layout — the same reason {@link VersionDiffTable}
 * is shared. Headers are passed in because each caller frames the columns
 * differently ("Current"/"After rollback" vs "Previous"/"Current").
 */
const CxIdDiffTable: React.FC<CxIdDiffTableProps> = ({
  previousCxIds,
  currentCxIds,
  previousHeader,
  currentHeader,
}) => {
  const { t } = useTranslation();
  return (
    <SpaceBetween size="xs">
      <Box fontWeight="bold">{t('string-meta-cx-ids')}</Box>
      <Table
        variant="embedded"
        ariaLabels={{ tableLabel: t('sgd-diff-cxid-table-aria') }}
        columnDefinitions={[
          {
            id: 'previous',
            header: previousHeader,
            cell: () => <CxIdBadgeList cxIds={previousCxIds} />,
          },
          {
            id: 'current',
            header: currentHeader,
            cell: () => <CxIdBadgeList cxIds={currentCxIds} />,
          },
        ]}
        items={CX_ID_TABLE_ITEMS}
        trackBy="id"
      />
    </SpaceBetween>
  );
};

export default CxIdDiffTable;
